import { InputHTMLAttributes, forwardRef, Ref, useId } from 'react';
import clsx from 'clsx';

interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Required for accessibility */
  label: string;
  /** Option to visually show or hide the label */
  showLabel?: boolean;
  /** `text` is the default and recommended type */
  type?: 'text' | 'email' | 'password' | 'search';
  placeholder?: string;
  /** Include for fields that require validation */
  error?: string;
  /** Variant for styling: 'client' (default) or 'admin' */
  variant?: 'client' | 'admin';
  /** Style adjustments for read only */
  readOnly?: boolean;
}

/**
 * @deprecated
 * Use the TextField component from @teachstonellc/component-library instead.
 */
function TextField(
  {
    label,
    showLabel = true,
    type = 'text',
    error,
    required,
    className,
    id,
    placeholder,
    variant = 'client',
    readOnly = false,
    ...props
  }: TextFieldProps,
  ref: Ref<HTMLInputElement>
) {
  const autoId = useId();
  const textFieldId = id ?? autoId;

  const variantStyles: Record<string, string> = {
    client: `p-2 font-sans text-base text-text-dark placeholder:text-charcoal-500 border border-navy-300 rounded-lg
        ${!readOnly && 'focus:outline-none focus:border-focus focus:ring-1 focus:ring-focus focus:ring-offset-0 placeholder:text-charcoal-400'}`,
    admin: `py-2 text-navy-900 placeholder:text-navy-400 border border-ds-border rounded
        ${!readOnly && 'p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ds-border-focus transition'}`
  };

  const textFieldClasses = clsx(
    variantStyles[variant] || variantStyles['client'],
    className,
    readOnly && 'border-none bg-inherit'
  );

  return (
    <div
      className={`flex flex-col gap-1 ${variant === 'client' && 'font-sans'}`}
    >
      {showLabel && (
        <label
          htmlFor={textFieldId}
          className={clsx(
            'font-semibold text-black',
            variant === 'client' && 'text-base'
          )}
        >
          {label}
          {required && <span className="ml-1">*</span>}
        </label>
      )}
      <input
        id={textFieldId}
        ref={ref}
        required={required}
        className={textFieldClasses}
        type={type}
        placeholder={placeholder}
        disabled={readOnly}
        {...props}
      />
      {error && (
        <p className="font-semibold text-sm text-error m-0 px-1">{error}</p>
      )}
    </div>
  );
}

// Explicitly setting `displayName` helps Storybook
// extract props from components that use forwardRef
const ForwardedTextField = forwardRef(TextField);
ForwardedTextField.displayName = 'TextField';

export { ForwardedTextField as TextField };
